import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { token } from '@atlaskit/tokens'
import Button from '@atlaskit/button'
import Tooltip from '@atlaskit/tooltip'
import GraphLineIcon from '@atlaskit/icon/glyph/graph-line'
import Breadcrumbs, { BreadcrumbsItem } from '@atlaskit/breadcrumbs'
import { selectTestScenario } from '../store/slices/testScenarioSlice'
import { selectNavigationContext } from '../store/slices/navigationContextSlice'
import StatusProgressBar from './reports/charts/StatusProgressBar'
import { aggregateScenarioStatusCounts } from '../utils/dataTransformer'

/**
 * HEADER SECTION
 *
 * Top navigation bar showing breadcrumb, execution status indicators,
 * and action buttons (Report, New Run).
 *
 * Supports two modes:
 * - Release mode (default): Shows progress bar, Report button, New Run button
 * - Library mode (isLibrary=true): Only shows breadcrumb, hides execution controls
 *
 * Breadcrumb format:
 * - Release scenarios: Project > Release > Folder Path > Scenario Key
 * - Library scenarios: Project > Library > Folder Path > Scenario Key
 *
 * @param {object} props
 * @param {function} props.onAddNewRun - Callback to open New Run modal
 * @param {function} props.onOpenReport - Callback to open Report modal
 * @param {Array} props.testCases - Array of test case objects with executions
 * @param {Array} props.testRuns - Array of test run objects
 * @param {boolean} props.isLibrary - If true, hides runs/reports (library template mode)
 * @param {object} props.selectedProject - Currently selected project { label, value }
 */
export function HeaderSection({ onAddNewRun, onOpenReport, testCases = [], testRuns = [], isLibrary = false, selectedProject }) {
  const navigate = useNavigate()
  const { releaseId } = useParams()
  const testScenario = useSelector(selectTestScenario)
  const navigationContext = useSelector(selectNavigationContext)

  // =========================================================================
  // BREADCRUMB NAVIGATION HANDLERS
  // =========================================================================

  /**
   * Navigate to project/releases page.
   * e.preventDefault() stops Atlaskit's default href="#" from interfering with React Router.
   */
  const handleProjectClick = (e) => {
    e.preventDefault()
    navigate('/releases')
  }

  /**
   * Navigate to library page.
   * e.preventDefault() stops Atlaskit's default href="#" from interfering with React Router.
   */
  const handleLibraryClick = (e) => {
    e.preventDefault()
    navigate('/library')
  }

  /**
   * Navigate to release's folder browser.
   * e.preventDefault() stops Atlaskit's default href="#" from interfering with React Router.
   */
  const handleReleaseClick = (e) => {
    e.preventDefault()
    navigate(`/releases/${releaseId}`)
  }

  /**
   * Navigate to folder browser with specific folder selected.
   * Uses query param to pre-select the folder.
   * e.preventDefault() stops Atlaskit's default href="#" from interfering with React Router.
   */
  const handleFolderClick = (e, folderId) => {
    e.preventDefault()
    if (isLibrary) {
      navigate(`/library?folder=${folderId}`)
    } else {
      navigate(`/releases/${releaseId}?folder=${folderId}`)
    }
  }

  /**
   * AGGREGATE STATUS COUNTS ACROSS ALL RUNS
   *
   * Memoized calculation of status distribution across all test runs.
   * This aggregates both test case and test step executions.
   */
  const statusCounts = useMemo(() => {
    return aggregateScenarioStatusCounts(testCases, testRuns)
  }, [testCases, testRuns])

  /**
   * CALCULATE COMPLETION RATIO
   *
   * Executed count (any status except "Not Run") / Total count.
   * Shows how many test cases/steps have been executed regardless of result.
   */
  const completionStats = useMemo(() => {
    const total = Object.values(statusCounts).reduce((sum, count) => sum + count, 0)
    const notRunCount = statusCounts['Not Run'] || 0
    const executedCount = total - notRunCount
    return { executedCount, total }
  }, [statusCounts])

  return (
    <div className="header-section">
      {/* Left side - Breadcrumb */}
      {/* Displays full navigation hierarchy: Project > Release/Library > Folders > Scenario */}
      <div className="breadcrumb">
        {isLibrary ? (
          // Library scenario breadcrumb: Project > Library > Folder Path > Scenario
          <Breadcrumbs>
            {/* Project - navigates to releases page */}
            <BreadcrumbsItem
              text={selectedProject?.key || 'Project'}
              onClick={handleProjectClick}
            />
            {/* Library */}
            <BreadcrumbsItem
              text="Library"
              onClick={handleLibraryClick}
            />
            {/* Folder hierarchy (if any) */}
            {navigationContext?.folderPath?.map((folder) => (
              <BreadcrumbsItem
                key={folder.id}
                text={folder.name}
                onClick={(e) => handleFolderClick(e, folder.id)}
              />
            ))}
            {/* Current scenario - preventDefault stops Atlaskit's default href="#" from blanking the route. */}
            <BreadcrumbsItem
              text={testScenario.scenarioKey || 'Scenario'}
              onClick={(e) => e.preventDefault()}
            />
          </Breadcrumbs>
        ) : (
          // Release scenario breadcrumb: Project > Releases > Release > Folder Path > Scenario
          <Breadcrumbs>
            {/* Project - navigates to releases page */}
            <BreadcrumbsItem
              text={selectedProject?.key || 'Project'}
              onClick={handleProjectClick}
            />
            {/* Releases - intermediate segment, mirrors FolderBrowser breadcrumb; navigates to releases list */}
            <BreadcrumbsItem
              text="Releases"
              onClick={handleProjectClick}
            />
            {/* Release - navigates to release's folder browser */}
            <BreadcrumbsItem
              text={navigationContext?.currentRelease?.name || 'Release'}
              onClick={handleReleaseClick}
            />
            {/* Folder hierarchy (if any) */}
            {navigationContext?.folderPath?.map((folder) => (
              <BreadcrumbsItem
                key={folder.id}
                text={folder.name}
                onClick={(e) => handleFolderClick(e, folder.id)}
              />
            ))}
            {/* Current scenario - preventDefault stops Atlaskit's default href="#" from blanking the route. */}
            <BreadcrumbsItem
              text={testScenario.scenarioKey || 'Scenario'}
              onClick={(e) => e.preventDefault()}
            />
          </Breadcrumbs>
        )}
      </div>

      {/* Right side - Execution Status (hidden in library mode) */}
      {!isLibrary && (
        <div className="progress-container">
          {/* Dynamic progress bar using StatusProgressBar component - tooltip shows breakdown on hover */}
          <div className="progress-bar" style={{ width: '200px' }}>
            <StatusProgressBar
              statusDistribution={statusCounts}
              height={8}
              showTooltip={true}
            />
          </div>

          {/* Dynamic completion ratio - executed vs total */}
          <div className="completion-ratio">
            {completionStats.executedCount}/{completionStats.total}
          </div>

          {/* Report Button - Opens execution summary report modal */}
          <Tooltip content="View Execution Report">
            <Button
              appearance="subtle"
              iconBefore={<GraphLineIcon label="Report" size="medium" />}
              onClick={onOpenReport}
              style={{ marginRight: '8px' }}
            >
              Report
            </Button>
          </Tooltip>

          <button className="new-run-button" onClick={onAddNewRun}>
            + New Run
          </button>
        </div>
      )}

      {/* Library mode indicator */}
      {isLibrary && (
        <div className="library-indicator" style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          color: 'var(--ds-text-subtlest, #6B778C)',
          fontSize: '12px'
        }}>
          <span style={{
            backgroundColor: 'var(--ds-background-discovery, #EAE6FF)',
            color: 'var(--ds-text-discovery, #403294)',
            padding: '2px 8px',
            borderRadius: '3px',
            fontWeight: 500
          }}>
            Library Template
          </span>
        </div>
      )}
    </div>
  )
}
